import React, { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, ConfirmDialog } from "@/components/Primitives";
import { api, fmtNumber, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// A damage record's lifecycle status maps to a clear, colour-coded label.
const STATUS_META = {
  repairable: { label: "Damaged", cls: "bg-amber-50 text-amber-800 border border-amber-200" },
  write_off: { label: "Written off", cls: "bg-red-50 text-red-800 border border-red-200" },
  salvaged: { label: "Salvaged", cls: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
  returned: { label: "Returned", cls: "bg-stone-100 text-stone-700 border border-stone-200" },
  sold_as_damaged: { label: "Sold as Damaged", cls: "bg-stone-100 text-stone-700 border border-stone-200" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: (status || "—").replace(/_/g, " "), cls: "bg-stone-100 text-stone-700 border border-stone-200" };
  return <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

export default function Damages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [delItem, setDelItem] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const r = await api.get("/damages");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((d) => {
    if (!q) return true;
    return [
      d.delivery_note_number, d.product_name, d.colour, d.origin,
      d.damage_reason, d.notes, d.bundle_number,
    ].some((v) => v && String(v).toLowerCase().includes(q.toLowerCase()));
  });

  const doDelete = async () => {
    try {
      await api.delete(`/damages/${delItem.id}`);
      toast.success("Damage record removed — linked stock restored to available");
      setDelItem(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const totals = {
    qty: filtered.reduce((a, x) => a + (Number(x.quantity_damaged) || 0), 0),
    salvaged: filtered.filter((x) => x.status === "salvaged").reduce((a, x) => a + (Number(x.quantity_damaged) || 0), 0),
    written: filtered.filter((x) => x.status === "write_off").reduce((a, x) => a + (Number(x.quantity_damaged) || 0), 0),
    wasteLen: filtered.reduce((a, x) => a + (Number(x.waste_length) || 0), 0),
    wasteWt: filtered.reduce((a, x) => a + (Number(x.waste_weight) || 0), 0),
  };

  return (
    <AppLayout>
      <PageHeader
        title="Damage Register"
        subtitle="Every pole marked damaged across Receiving, Stock & Processing — with its original receipt details, outcome and salvage waste. Log damage from the line itself, not here."
        actions={
          <Btn variant="secondary" as="a" href={`${API}/exports/damages.xlsx`} target="_blank" data-testid="damages-export-btn">
            <Download className="h-4 w-4" /> Export
          </Btn>
        }
      />

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search by DN, product, reason, origin..." />
        <div className="text-xs text-stone-500 ml-auto flex flex-wrap gap-x-4 gap-y-1 justify-end" data-testid="damages-summary">
          <span>{filtered.length} records · {fmtNumber(totals.qty)} pcs</span>
          <span className="text-emerald-700">{fmtNumber(totals.salvaged)} salvaged</span>
          <span className="text-red-700">{fmtNumber(totals.written)} written off</span>
          <span>Waste: {fmtNumber(totals.wasteLen, 2)} m · {fmtNumber(totals.wasteWt, 2)} kg</span>
        </div>
      </Toolbar>

      <DataTable
        columns={[
          { header: "Date", key: "damage_date", mono: true },
          { header: "Delivery Note", render: (r) => <span className="font-mono text-xs">{r.delivery_note_number || "—"}</span> },
          { header: "Product", render: (r) => (
            <div>
              <div className="text-sm">{r.product_name || "—"}</div>
              <div className="text-xs text-stone-500 font-mono">
                {r.butt_size != null ? `${r.butt_size}mm` : "—"} · {r.length ? `${r.length}m` : "—"} · {r.colour || "—"}
              </div>
            </div>
          )},
          { header: "Origin", key: "origin", render: (r) => r.origin || "—" },
          { header: "Qty", align: "right", mono: true, render: (r) => fmtNumber(r.quantity_damaged) },
          { header: "Reason", render: (r) => <span className="text-sm">{r.damage_reason || "—"}</span> },
          { header: "Status", render: (r) => <StatusPill status={r.status} /> },
          { header: "Waste", align: "right", mono: true, render: (r) => (
            (r.waste_length || r.waste_weight)
              ? <span className="text-xs text-amber-800">{r.waste_length ? `${fmtNumber(r.waste_length, 2)}m` : "—"}{r.waste_weight ? ` · ${fmtNumber(r.waste_weight, 2)}kg` : ""}</span>
              : <span className="text-stone-400">—</span>
          )},
          ...(isAdmin ? [{ header: "", align: "right", render: (r) => (
            <button onClick={() => setDelItem(r)} className="text-stone-500 hover:text-red-700" title="Remove / undo damage" data-testid={`damage-delete-${r.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}] : []),
        ]}
        rows={filtered}
        empty="No damage records yet. Mark a pole damaged from Receiving, Stock on Hand or Processing."
      />

      <ConfirmDialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={doDelete}
        title="Remove Damage Record"
        confirmLabel="Remove"
        testid="damage-delete-confirm"
        message={delItem ? `Remove this damage record (${delItem.quantity_damaged} pcs · ${delItem.damage_reason || "no reason"})? If the linked stock is still flagged 'damaged', it will be restored to 'available'. This cannot be undone.` : ""}
      />
    </AppLayout>
  );
}
